import React, { useState } from 'react';
import { AlertTriangle, ExternalLink, Eye, MoreHorizontal, Repeat, XCircle, CheckCircle, AlertCircle, Wallet } from 'lucide-react';
import { Link, router } from '@inertiajs/react';
import { AdminLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  Breadcrumbs,
  Badge,
  Button,
  Card,
  CardHeader,
  CardBody,
  DescriptionList,
  DataTable,
  Dropdown,
  Tabs,
  TabPanel,
  ConfirmDialog,
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Select,
  Textarea,
  Toggle,
  Input,
  StatCard,
  Collapsible,
} from '../../../design-system/components';
import { formatCurrency, formatDate, formatDateTime } from '../../../lib/formatters';
import {
  admin_affiliates_path,
  approve_admin_affiliate_path,
  deny_admin_affiliate_path,
  deactivate_admin_affiliate_path,
  reassign_campaign_admin_affiliate_path,
  toggle_auto_payout_admin_affiliate_path,
  impersonate_admin_affiliate_path,
  update_paypal_admin_affiliate_path,
  confirm_paypal_admin_affiliate_path,
} from '../../../lib/routes';
const statusVariant = {
  approved: 'success',
  pending: 'warning',
  denied: 'danger',
  deactivated: 'default',
};
const commissionStatusVariant = {
  pending: 'warning',
  paid: 'success',
  reversed: 'danger',
};
const referralColumns = [
  {
    id: 'id',
    header: 'ID',
    render: (row) => <span className="font-mono text-xs">#{row.id}</span>,
  },
  {
    id: 'stripe_customer_id',
    header: 'Customer',
    render: (row) => (
      <span className="flex items-center gap-1.5">
        <span className={row.customer_email ? "text-sm" : "font-mono text-xs text-fg-tertiary"}>
          {row.customer_email || row.stripe_customer_id || '\u2014'}
        </span>
        {row.stripe_customer_id && (
          <a
            href={`https://dashboard.stripe.com/customers/${row.stripe_customer_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-fg-tertiary hover:text-fg-secondary"
          >
            <ExternalLink size={14} />
          </a>
        )}
      </span>
    ),
  },
  {
    id: 'attribution_type',
    header: 'Attribution',
    render: (row) => <span className="capitalize">{row.attribution_type.replace('_', ' ')}</span>,
  },
  {
    id: 'referred_at',
    header: 'Referred',
    sortable: true,
    render: (row) => formatDate(row.referred_at),
  },
  {
    id: 'converted_at',
    header: 'Converted',
    sortable: true,
    render: (row) =>
      row.converted_at ? (
        <Badge variant="success" size="sm">
          {formatDate(row.converted_at)}
        </Badge>
      ) : (
        <span className="text-fg-tertiary">Not yet</span>
      ),
  },
];
const commissionColumns = [
  {
    id: 'id',
    header: 'ID',
    render: (row) => <span className="font-mono text-xs">#{row.id}</span>,
  },
  {
    id: 'customer_email',
    header: 'Customer',
    render: (row) => (
      <span className="text-sm text-fg-secondary">
        {row.customer_email || '\u2014'}
      </span>
    ),
  },
  {
    id: 'commission_amount_cents',
    header: 'Amount',
    sortable: true,
    render: (row) => (
      <span className="font-medium">{formatCurrency(row.commission_amount_cents)}</span>
    ),
  },
  {
    id: 'invoice_amount_cents',
    header: 'Invoice',
    render: (row) => (
      <span className="flex items-center gap-1.5">
        {formatCurrency(row.invoice_amount_cents)}
        {row.stripe_invoice_id && (
          <a
            href={`https://dashboard.stripe.com/invoices/${row.stripe_invoice_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-fg-tertiary hover:text-fg-secondary"
          >
            <ExternalLink size={14} />
          </a>
        )}
      </span>
    ),
  },
  {
    id: 'billing_interval',
    header: 'Interval',
    render: (row) => <span className="capitalize">{row.billing_interval}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    render: (row) => (
      <Badge variant={commissionStatusVariant[row.status] ?? 'default'} size="sm">
        {row.status}
      </Badge>
    ),
  },
  {
    id: 'created_at',
    header: 'Date',
    sortable: true,
    render: (row) => formatDate(row.created_at),
  },
];
const fraudEventColumns = [
  {
    id: 'id',
    header: 'ID',
    render: (row) => <span className="font-mono text-xs">#{row.id}</span>,
  },
  {
    id: 'rule_type',
    header: 'Rule Type',
    render: (row) => <span className="capitalize">{row.rule_type.replace(/_/g, ' ')}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    render: (row) => {
      const variant =
        row.status === 'confirmed' ? 'danger' : row.status === 'cleared' ? 'success' : 'warning';
      return (
        <Badge variant={variant} size="sm">
          {row.status}
        </Badge>
      );
    },
  },
  {
    id: 'created_at',
    header: 'Date',
    sortable: true,
    render: (row) => formatDate(row.created_at),
  },
];
export default function Show({
  affiliate,
  referrals,
  commissions,
  fraud_events,
  answers,
  campaigns,
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const [actionDialog, setActionDialog] = useState(null);
  const [denialReason, setDenialReason] = useState('');
  const [reassignCampaignId, setReassignCampaignId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [autoPayoutToggling, setAutoPayoutToggling] = useState(false);
  const [editingPaypal, setEditingPaypal] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState(affiliate.paypal_email || '');
  const [paypalSaving, setPaypalSaving] = useState(false);
  const [confirmingPaypal, setConfirmingPaypal] = useState(false);
  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
    },
    {
      id: 'referrals',
      label: 'Referrals',
      count: referrals.length,
    },
    {
      id: 'commissions',
      label: 'Commissions',
      count: commissions.length,
    },
    {
      id: 'fraud',
      label: 'Fraud Events',
      count: fraud_events.length,
    },
  ];
  const affiliateDetails = [
    { term: 'Name', detail: affiliate.name },
    { term: 'Email', detail: affiliate.email },
    {
      term: 'Status',
      detail: (
        <Badge variant={statusVariant[affiliate.status] ?? 'default'}>{affiliate.status}</Badge>
      ),
    },
    { term: 'Campaign', detail: affiliate.campaign?.name || '\u2014' },
    {
      term: 'Slug',
      detail: affiliate.slug ? (
        <span className="font-mono text-sm">{affiliate.slug}</span>
      ) : (
        '\u2014'
      ),
    },
    {
      term: 'Coupon Code',
      detail: affiliate.coupon_code ? (
        <span className="font-mono text-sm">{affiliate.coupon_code}</span>
      ) : (
        '\u2014'
      ),
    },
    { term: 'Applied', detail: formatDateTime(affiliate.created_at) },
  ];
  const paypalDetails = [
    {
      term: 'PayPal Email',
      detail: (
        <div className="space-y-1.5">
          {editingPaypal ? (
            <div className="flex items-center gap-2">
              <Input
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                type="email"
                placeholder="paypal@example.com"
                className="w-56"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setPaypalSaving(true);
                    router.patch(
                      update_paypal_admin_affiliate_path(affiliate.id),
                      { paypal_email: paypalEmail },
                      {
                        onFinish: () => {
                          setPaypalSaving(false);
                          setEditingPaypal(false);
                        },
                      },
                    );
                  } else if (e.key === 'Escape') {
                    setPaypalEmail(affiliate.paypal_email || '');
                    setEditingPaypal(false);
                  }
                }}
              />
              <Button
                size="sm"
                variant="secondary"
                loading={paypalSaving}
                onClick={() => {
                  setPaypalSaving(true);
                  router.patch(
                    update_paypal_admin_affiliate_path(affiliate.id),
                    { paypal_email: paypalEmail },
                    {
                      onFinish: () => {
                        setPaypalSaving(false);
                        setEditingPaypal(false);
                      },
                    },
                  );
                }}
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setPaypalEmail(affiliate.paypal_email || '');
                  setEditingPaypal(false);
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span>{affiliate.paypal_email || '\u2014'}</span>
              <Button
                variant="ghost"
                size="sm"
                className="!px-1.5 !py-0.5 text-xs"
                onClick={() => setEditingPaypal(true)}
              >
                Edit
              </Button>
            </div>
          )}
          {affiliate.unconfirmed_paypal_email && (
            <div className="flex items-center gap-2">
              <Badge variant="warning" size="sm">
                <AlertCircle size={12} className="mr-1" />
                Pending: {affiliate.unconfirmed_paypal_email}
              </Badge>
              <Button
                size="sm"
                variant="outline"
                loading={confirmingPaypal}
                onClick={() => {
                  setConfirmingPaypal(true);
                  router.post(
                    confirm_paypal_admin_affiliate_path(affiliate.id),
                    {},
                    { onFinish: () => setConfirmingPaypal(false) },
                  );
                }}
              >
                <CheckCircle size={14} className="mr-1" />
                Confirm
              </Button>
            </div>
          )}
        </div>
      ),
    },
    {
      term: 'Auto-Payout',
      detail: (
        <div className="flex items-center gap-2">
          <Toggle
            checked={affiliate.auto_payout}
            onChange={() => {
              setAutoPayoutToggling(true);
              router.post(
                toggle_auto_payout_admin_affiliate_path(affiliate.id),
                {},
                { onFinish: () => setAutoPayoutToggling(false) },
              );
            }}
            disabled={autoPayoutToggling || (!affiliate.auto_payout && !affiliate.paypal_email)}
          />
          {!affiliate.auto_payout && !affiliate.paypal_email && (
            <span className="text-xs text-fg-tertiary">Requires PayPal email</span>
          )}
        </div>
      ),
    },
  ];
  function executeAction() {
    if (!actionDialog) return;
    setActionLoading(true);
    const onFinish = () => {
      setActionLoading(false);
      setActionDialog(null);
      setDenialReason('');
      setReassignCampaignId('');
    };
    switch (actionDialog) {
      case 'approve':
        router.post(
          approve_admin_affiliate_path(affiliate.id),
          {},
          {
            onFinish,
          },
        );
        break;
      case 'deny':
        router.post(
          deny_admin_affiliate_path(affiliate.id),
          {
            denial_reason: denialReason,
          },
          {
            onFinish,
          },
        );
        break;
      case 'deactivate':
        router.post(
          deactivate_admin_affiliate_path(affiliate.id),
          {},
          {
            onFinish,
          },
        );
        break;
      case 'reassign':
        router.patch(
          reassign_campaign_admin_affiliate_path(affiliate.id),
          {
            campaign_id: reassignCampaignId,
          },
          {
            onFinish,
          },
        );
        break;
    }
  }
  const overflowItems = [
    {
      label: 'Reassign Campaign',
      icon: <Repeat size={16} />,
      onClick: () => setActionDialog('reassign'),
    },
    ...(affiliate.status === 'approved'
      ? [
          { type: 'divider' },
          {
            label: 'Deactivate',
            icon: <XCircle size={16} />,
            destructive: true,
            onClick: () => setActionDialog('deactivate'),
          },
        ]
      : []),
  ];

  const actionButtons = (
    <div className="flex items-center gap-2">
      {affiliate.status === 'pending' && (
        <>
          <Button size="sm" onClick={() => setActionDialog('approve')}>
            Approve
          </Button>
          <Button size="sm" variant="danger" onClick={() => setActionDialog('deny')}>
            Deny
          </Button>
        </>
      )}
      {affiliate.status === 'approved' && affiliate.onboarded && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => router.post(impersonate_admin_affiliate_path(affiliate.id))}
        >
          <Eye size={16} className="mr-1.5" />
          View Dashboard
        </Button>
      )}
      <Dropdown
        trigger={
          <Button variant="outline" size="sm" aria-label="More actions">
            <MoreHorizontal size={16} />
          </Button>
        }
        items={overflowItems}
        align="right"
      />
    </div>
  );
  return (
    <AdminLayout>
      <Breadcrumbs
        items={[
          { label: 'Affiliates', href: admin_affiliates_path() },
          { label: affiliate.name },
        ]}
      />
      <PageHeader
        title={affiliate.name}
        description={
          <span className="inline-flex items-center gap-2">
            {affiliate.email}
            <Badge variant={statusVariant[affiliate.status] ?? 'default'} size="sm">
              {affiliate.status}
            </Badge>
          </span>
        }
        actions={actionButtons}
      />

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Total Referrals" value={affiliate.referral_count} />
        <StatCard label="Total Commissions" value={formatCurrency(affiliate.commission_total_cents)} />
        <StatCard label="Pending" value={formatCurrency(affiliate.pending_commission_cents)} />
        <StatCard label="Paid" value={formatCurrency(affiliate.paid_commission_cents)} />
      </div>

      <div className="mt-6">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        <TabPanel tabId="overview" activeTab={activeTab}>
          <div className="space-y-6">
            <Collapsible title="Affiliate Details" defaultOpen>
              <DescriptionList items={affiliateDetails} columns={3} />
            </Collapsible>

            <Collapsible title="PayPal & Payouts" defaultOpen>
              <DescriptionList items={paypalDetails} columns={2} />
            </Collapsible>

            {answers.length > 0 && (
              <Collapsible title="Application Answers" defaultOpen>
                <dl className="space-y-4">
                  {answers.map((answer) => (
                    <div key={answer.id}>
                      <dt className="text-sm font-medium text-fg-secondary">
                        {answer.question_text}
                      </dt>
                      <dd className="mt-1 text-sm text-fg">{answer.answer_text}</dd>
                    </div>
                  ))}
                </dl>
              </Collapsible>
            )}
          </div>
        </TabPanel>

        <TabPanel tabId="referrals" activeTab={activeTab}>
          <Card>
            <CardBody className="p-0">
              <DataTable
                columns={referralColumns}
                data={referrals}
                keyAccessor="id"
                emptyTitle="No referrals yet"
                emptyDescription="Referrals from this affiliate will appear here."
                className="border-0 rounded-none"
              />
            </CardBody>
          </Card>
        </TabPanel>

        <TabPanel tabId="commissions" activeTab={activeTab}>
          <Card>
            <CardBody className="p-0">
              <DataTable
                columns={commissionColumns}
                data={commissions}
                keyAccessor="id"
                emptyTitle="No commissions yet"
                emptyDescription="Commissions earned by this affiliate will appear here."
                className="border-0 rounded-none"
              />
            </CardBody>
          </Card>
        </TabPanel>

        <TabPanel tabId="fraud" activeTab={activeTab}>
          <Card>
            <CardBody className="p-0">
              <DataTable
                columns={fraudEventColumns}
                data={fraud_events}
                keyAccessor="id"
                emptyTitle="No fraud events"
                emptyDescription="No fraud events have been flagged for this affiliate."
                className="border-0 rounded-none"
              />
            </CardBody>
          </Card>
        </TabPanel>
      </div>

      {/* Approve dialog */}
      {actionDialog === 'approve' && (
        <ConfirmDialog
          open
          onClose={() => setActionDialog(null)}
          onConfirm={executeAction}
          title="Approve Affiliate"
          message={`Are you sure you want to approve ${affiliate.name}? They will be able to start referring customers.`}
          confirmLabel="Approve"
          loading={actionLoading}
        />
      )}

      {/* Deny dialog with reason */}
      {actionDialog === 'deny' && (
        <Modal
          open
          onClose={() => {
            setActionDialog(null);
            setDenialReason('');
          }}
          size="sm"
        >
          <ModalTitle>
            <span className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-danger-500" aria-hidden="true" />
              Deny Affiliate
            </span>
          </ModalTitle>
          <ModalBody>
            <p className="mb-3 text-sm text-fg-secondary">
              Are you sure you want to deny {affiliate.name}?
            </p>
            <Textarea
              label="Denial Reason (optional)"
              value={denialReason}
              onChange={(e) => setDenialReason(e.target.value)}
              placeholder="Provide a reason for denial..."
              rows={3}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setActionDialog(null);
                setDenialReason('');
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={executeAction} loading={actionLoading}>
              Deny
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Deactivate dialog */}
      {actionDialog === 'deactivate' && (
        <ConfirmDialog
          open
          onClose={() => setActionDialog(null)}
          onConfirm={executeAction}
          title="Deactivate Affiliate"
          message={`Are you sure you want to deactivate ${affiliate.name}? This will reverse any pending commissions and deactivate their coupon code.`}
          confirmLabel="Deactivate"
          destructive
          loading={actionLoading}
        />
      )}

      {/* Reassign campaign dialog */}
      {actionDialog === 'reassign' && (
        <Modal
          open
          onClose={() => {
            setActionDialog(null);
            setReassignCampaignId('');
          }}
          size="sm"
        >
          <ModalTitle>Reassign Campaign</ModalTitle>
          <ModalBody>
            <p className="mb-3 text-sm text-fg-secondary">
              Select a new campaign for {affiliate.name}:
            </p>
            <Select
              label="Campaign"
              value={reassignCampaignId}
              onChange={(e) => setReassignCampaignId(e.target.value)}
            >
              <option value="">Select a campaign...</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setActionDialog(null);
                setReassignCampaignId('');
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={executeAction}
              loading={actionLoading}
              disabled={!reassignCampaignId}
            >
              Reassign
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </AdminLayout>
  );
}
